import { type ApiErrorResponse, isApiErrorResponse } from "~/types/api-error"

export default defineNuxtPlugin((nuxtApp) => {
	const notificationStore = useApiErrorNotifications()
	const last = { msg: "", at: 0 }
	function shouldSkip(msg: string, ms = 1500) {
		const now = Date.now()
		if (last.msg === msg && now - last.at < ms) return true
		last.msg = msg
		last.at = now
		return false
	}

	const toMessage = (err: unknown): string => {
		if (err instanceof Error) return err.message || err.toString()
		if (hasProp(err, "data") && isApiErrorResponse(err.data)) {
			return err.data?.message || JSON.stringify(err)
		}
		if (typeof err === "string") return err
		try {
			return JSON.stringify(err)
		} catch {
			return String(err)
		}
	}

	const push = (err: unknown, context: string) => {
		const msg = toMessage(err)
		if (shouldSkip(`${context}:${msg}`)) return
		const isApiError = hasProp(err, "data") && isApiErrorResponse(err.data)
		notificationStore.addApiError({
			errorCode: isApiError ? (err.data as ApiErrorResponse).errorCode || "Unknown Error" : "Application Error",
			message: `${msg}`,
			timestamp: isApiError ? (err.data as ApiErrorResponse).timestamp || Date.now() : Date.now(),
			traceId: isApiError ? (err.data as ApiErrorResponse).traceId || "n/a" : "n/a",
			statusCode: isApiError ? (err.data as ApiErrorResponse).statusCode : 1
		})
	}

	// 1) Catch all Vue component errors
	nuxtApp.vueApp.config.errorHandler = (error, _instance, info) => {
		push(error, info || "vue")
	}

	// 2) Nuxt top-level Vue error hook
	nuxtApp.hook("vue:error", (error, _instance, info) => {
		push(error, info || "vue:error")
	})

	// 3) Browser global errors and unhandled promise rejections
	globalThis.addEventListener("error", (event) => {
		if (event.message?.includes?.("ResizeObserver loop completed with undelivered notifications")) {
			return
		}
		push(event.error || event.message, "window:error")
	})
	globalThis.addEventListener("unhandledrejection", (event) => {
		push(event.reason, "unhandledrejection")
	})
})
